import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { CalendarDays, ChevronDown, ChevronUp, Plus, X, Bus } from 'lucide-react';
import { DevisChambre } from '../components/devis/DevisChambre';
import { DevisRecap, DevisPrint, buildRecap } from '../components/devis/DevisRecap';
import { calcDevis, calcRoom, newRoom, newTraveler, fmtDateFr } from '../components/devis/devisCalc';

// Iter 684 — semaines/prix lus dans les déclinaisons Lloret (API /devis-espagne/config) ; plus rien de codé en dur
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REGLES_DEFAUT = { age_bebe: 3, age_enfant: 12, assurance: 20, adhesion: 10, note: '' };
const PICKUPS = [['st-etienne', 'Saint-Étienne'], ['st-priest', 'Saint-Priest'], ['villeurbanne', 'Villeurbanne'], ['autoroute', "Aire d'autoroute (A7/A9)"]];
const Y = 'bg-[#FFE066] border-2 border-[#F5C400] text-[#1E3A5F] font-bold rounded-xl';

const libre = (r) => r === -1 || r === undefined || r === null || r >= 10 ** 6;
const weekComplet = (w) => {
  const ds = w.dispo?.sans, da = w.dispo?.avec;
  const sansOk = !ds || libre(ds.restantes) || ds.restantes > 0;
  const avecOk = !da || (da.trajets ? (libre(da.trajets.aller.restantes) || da.trajets.aller.restantes > 0 || libre(da.trajets.retour.restantes) || da.trajets.retour.restantes > 0) : (libre(da.restantes) || da.restantes > 0));
  return !sansOk && !avecOk;
};
const plTxt = (r) => (libre(r) ? 'disponible' : r <= 0 ? 'COMPLET' : `${r} pl.`);
const compteurTxt = (w) => {
  const ds = w.dispo?.sans, da = w.dispo?.avec;
  const parts = [];
  if (ds) parts.push(`sans car : ${plTxt(ds.restantes)}`);
  if (da) parts.push(da.trajets ? (w.period === 'mai'
    ? `car aller-retour : ${plTxt(libre(da.trajets.aller.restantes) && libre(da.trajets.retour.restantes) ? -1 : Math.min(libre(da.trajets.aller.restantes) ? 10 ** 6 : da.trajets.aller.restantes, libre(da.trajets.retour.restantes) ? 10 ** 6 : da.trajets.retour.restantes))}`
    : `car aller : ${plTxt(da.trajets.aller.restantes)} · car retour : ${plTxt(da.trajets.retour.restantes)}`) : `avec car : ${plTxt(da.restantes)}`);
  return parts.join(' · ');
};

const Step = ({ n, title, sub, right, children, testid }) => (
  <section className="mb-6" data-testid={testid}>
    <div className="flex items-center justify-between gap-3 mb-3">
      <h2 className="flex items-center gap-3 text-lg md:text-xl font-black text-[#1E3A5F]">
        <span className="w-9 h-9 rounded-full bg-[#1E3A5F] text-white flex items-center justify-center text-base font-black flex-shrink-0">{n}</span>
        <span>{title} {sub && <span className="font-medium text-gray-500 text-base">{sub}</span>}</span>
      </h2>
      {right}
    </div>
    {children}
  </section>
);

const SpainSimulator = () => {
  const [config, setConfig] = useState(null);
  const [weeksAll, setWeeksAll] = useState([]);
  const [regles, setRegles] = useState(REGLES_DEFAUT);
  const [selectedIds, setSelectedIds] = useState([]);
  const [rooms, setRooms] = useState([newRoom(true, true), newRoom(false, true)]);
  const [pickupPoint, setPickupPoint] = useState('');
  const [customPickup, setCustomPickup] = useState('');
  const [withInsurance, setWithInsurance] = useState(false);

  useEffect(() => {
    axios.get(`${API}/devis-espagne/config`).then((r) => {
      setWeeksAll((r.data.semaines || []).map((w) => ({ ...w, transport: w.transport ?? 0, label: `${w.label} ${w.year}` })));
      setRegles({ ...REGLES_DEFAUT, ...(r.data.regles || {}) });
      setConfig(r.data);
    }).catch(() => setConfig({ semaines: [], erreur: true }));
  }, []);

  const weeks = useMemo(() => selectedIds.map((id) => weeksAll.find((w) => w.id === id)).filter(Boolean), [selectedIds, weeksAll]);
  const last = weeks[weeks.length - 1];
  const suivante = last && last.period !== 'mai' ? weeksAll.find((w) => w.start === last.end && !weekComplet(w)) : null;

  const patchRoom = (idx, patch) => setRooms((prev) => prev.map((r, i) => {
    if (i !== idx) return r;
    const next = { ...r, ...patch };
    while (next.travelers.length < next.nb) next.travelers = [...next.travelers, newTraveler(true)];
    return next;
  }));
  const patchTraveler = (idx, ti, patch) => setRooms((prev) => prev.map((r, i) => (i !== idx ? r : { ...r, travelers: r.travelers.map((t, j) => (j === ti ? { ...t, ...patch } : t)) })));

  const devis = weeks.length ? calcDevis(rooms, weeks, regles, withInsurance) : null;
  const pickupLabel = pickupPoint === 'autoroute' ? `Aire d'autoroute — ${customPickup || 'à préciser'}` : (PICKUPS.find((p) => p[0] === pickupPoint)?.[1] || 'à préciser');
  const pickupManquant = devis?.hasCar && (!pickupPoint || (pickupPoint === 'autoroute' && !customPickup.trim()));
  const recap = devis && devis.complete && !devis.tooManyAdults ? buildRecap(devis, weeks, { pickupLabel, withInsurance, regles }) : null;
  const prefill = recap ? (() => {
    const voyageurs = []; const chambres = [];
    devis.chambres.forEach((c) => {
      const occupants = c.travs.map((t) => { voyageurs.push({ dateNaissance: t.dob, aller: !!t.aller, retour: !!t.retour }); return voyageurs.length - 1; });
      const n = occupants.length;
      const typeChambre = n === 1 ? 'individuelle' : n === 2 ? (c.literie === 'couple' ? 'grand-lit' : c.literie === 'separes' ? '2-lits' : '') : `${n}-lits`;
      chambres.push({ occupants, typeChambre, vuePiscine: !!c.piscine });
    });
    const legs = { aller: voyageurs.some((v) => v.aller) && devis.aller > 0, retour: voyageurs.some((v) => v.retour) && devis.retour > 0 && weeks.length === 1 };
    const busTrajets = legs.aller && legs.retour ? 'aller_retour' : legs.aller ? 'aller_seul' : legs.retour ? 'retour_seul' : 'aucun';
    const notes = recap.chambres.map((c) => `Chambre ${c.num} : ${c.literie}${devis.chambres.find((x) => x.num === c.num)?.piscine ? ` Vue piscine garantie souhaitée (+${devis.chambres.find((x) => x.num === c.num).piscine} €).` : ''}`).join(' ');
    return { destination: weeks[0].ids?.sans || weeks[0].ids?.avec, sejour: weeks[0].label, semaines: weeks.length, voyageurs, chambres, busTrajets, lieuDepart: busTrajets !== 'aucun' ? pickupPoint : '', aireAutoroute: pickupPoint === 'autoroute' ? customPickup : '', assurance: withInsurance ? 'oui' : '', demandesSpeciales: `Devis simulateur : ${recap.total} €. ${notes}` };
  })() : null;
  const annee = config?.annee || '';

  return (
    <div className="bg-[#F6F8FB] min-h-screen" data-testid="spain-simulator-page">
      <style>{`@media print { body * { visibility: hidden !important; } #devis-print, #devis-print * { visibility: visible !important; } #devis-print { display: block !important; position: absolute; left: 0; top: 0; width: 100%; } }`}</style>
      <header className="bg-[#0F2D5C] text-white py-6 px-4 text-center print:hidden">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-wide" data-testid="simulator-title">HÔTEL OASIS PARK</h1>
        <p className="text-sm sm:text-base font-semibold tracking-[0.2em] mt-2 flex items-center justify-center gap-3"><span className="h-px w-10 bg-white/50" />VOTRE SÉJOUR<span className="h-px w-10 bg-white/50" /></p>
        <p className="text-base sm:text-lg font-black tracking-wide">LLORET DEL MAR {annee}</p>
        {config?.regles?.note && <p className="text-xs opacity-80 mt-2" data-testid="devis-note">{config.regles.note}</p>}
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 print:hidden">
        <Step n={1} title="Choisissez votre séjour" testid="step-week">
          {config && weeksAll.length === 0 && <p className="text-sm text-gray-500 italic" data-testid="devis-vide">Les dates de la prochaine saison ne sont pas encore ouvertes.</p>}
          {weeksAll.length > 0 && (
            <div className={`${Y} flex items-center gap-3 px-4 py-1`}>
              <CalendarDays size={22} className="flex-shrink-0" />
              <select value={selectedIds[0] || ''} onChange={(e) => setSelectedIds(e.target.value ? [e.target.value] : [])} className="flex-1 min-w-0 truncate bg-transparent py-3 text-xs sm:text-base font-bold outline-none appearance-none" data-testid="week-select">
                <option value="">— Sélectionnez vos dates —</option>
                {weeksAll.map((w) => <option key={w.id} value={w.id} disabled={weekComplet(w)}>{w.label.replace(/ \d{4}$/, '')} — {fmtDateFr(w.start)} au {fmtDateFr(w.end)} ({w.nights} nuits) — {w.basePrice} €{weekComplet(w) ? ' — COMPLET' : ''}</option>)}
              </select>
              <ChevronDown size={22} className="flex-shrink-0" />
            </div>
          )}
          {weeks.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              {weeks.map((w, i) => (
                <span key={w.id} className="inline-flex items-center gap-1 bg-white border border-gray-300 rounded-full px-3 py-1.5 font-semibold text-[#1E3A5F]" data-testid={`week-chip-${w.id}`}>
                  {w.label} · {w.basePrice} €{w.ids?.avec ? ` · car ${w.prixTrajet} €/trajet` : ' · sans car'}
                  {i > 0 && i === weeks.length - 1 && <button onClick={() => setSelectedIds((p) => p.slice(0, -1))} className="ml-1 text-gray-400 hover:text-red-600" aria-label="Retirer cette semaine" data-testid="week-remove"><X size={14} /></button>}
                </span>
              ))}
              {suivante && <button onClick={() => setSelectedIds((p) => [...p, suivante.id])} className="inline-flex items-center gap-1 rounded-full border-2 border-dashed border-[#1E3A5F]/40 px-3 py-1.5 font-semibold text-[#1E3A5F] hover:bg-white" data-testid="week-add-next"><Plus size={14} /> Ajouter la semaine suivante ({suivante.label.replace(/ \d{4}$/, '')})</button>}
              <span className="w-full text-emerald-700 font-semibold" data-testid="week-dispo">{weeks.map((w) => compteurTxt(w)).filter(Boolean).join(' | ')}</span>
              {weeks.length > 1 && <span className="w-full text-gray-600">Séjour de {devis?.nights} nuits — le car se règle par trajet : l'aller sur la 1re semaine, le retour sur la dernière.</span>}
            </div>
          )}
        </Step>

        {weeks.length > 0 && (
          <>
            <Step n={2} title="Chambre 1" testid="step-room-1">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5">
                <DevisChambre room={rooms[0]} idx={0} weeks={weeks} calc={calcRoom(rooms[0], weeks, regles)} onRoom={(p) => patchRoom(0, p)} onTraveler={(ti, p) => patchTraveler(0, ti, p)} />
              </div>
            </Step>

            <Step n={3} title="Chambre 2" sub="(optionnelle)" testid="step-room-2"
              right={<button onClick={() => patchRoom(1, { active: !rooms[1].active })} className={`inline-flex items-center gap-2 text-sm font-bold rounded-xl px-4 py-2 ${rooms[1].active ? 'bg-white border-2 border-gray-300 text-gray-700' : 'bg-[#1E3A5F] text-white'}`} data-testid="room2-toggle">
                {rooms[1].active ? <><ChevronUp size={16} /> Retirer la chambre 2</> : <><Plus size={16} /> Ajouter une chambre 2</>}
              </button>}>
              {rooms[1].active ? (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5">
                  <DevisChambre room={rooms[1]} idx={1} weeks={weeks} calc={calcRoom(rooms[1], weeks, regles)} onRoom={(p) => patchRoom(1, p)} onTraveler={(ti, p) => patchTraveler(1, ti, p)} />
                </div>
              ) : <p className="text-sm text-gray-500 bg-white rounded-2xl border border-dashed border-gray-300 px-4 py-4" data-testid="room2-closed">Chambre 2 non utilisée — elle n'est pas comptée dans le devis.</p>}
            </Step>

            {devis?.hasCar && (
              <section className={`mb-6 bg-white rounded-2xl shadow-sm border-2 p-4 md:p-5 ${pickupManquant ? 'border-red-400' : 'border-gray-200'}`} data-testid="pickup-section">
                <p className="font-black text-[#1E3A5F] flex items-center gap-2 mb-1"><Bus size={18} /> Point de départ du car</p>
                <p className={`text-xs mb-3 ${pickupManquant ? 'text-red-700 font-semibold' : 'text-gray-500'}`}>{pickupManquant ? 'Choisissez votre point de départ pour compléter le devis.' : 'Même point pour l\'aller et le retour.'}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {PICKUPS.map(([k, l]) => (
                    <button key={k} onClick={() => { setPickupPoint(k); if (k !== 'autoroute') setCustomPickup(''); }} className={`rounded-xl px-3 py-3 text-sm font-bold border-2 transition-colors ${pickupPoint === k ? 'bg-[#1E3A5F] border-[#1E3A5F] text-white' : 'bg-white border-gray-300 text-[#1E3A5F]'}`} data-testid={`pickup-${k}`}>{l}</button>
                  ))}
                </div>
                {pickupPoint === 'autoroute' && <input type="text" value={customPickup} onChange={(e) => setCustomPickup(e.target.value)} placeholder="Nom de l'aire d'autoroute souhaitée" className={`${Y} w-full mt-3 px-4 py-3 text-sm`} data-testid="pickup-custom-input" />}
              </section>
            )}

            <Step n={4} title="Votre devis" testid="step-devis">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-5">
                <p className="text-sm text-[#1E3A5F] mb-4"><strong>{weeks.map((w) => w.label).join(' + ')}</strong> — du {fmtDateFr(weeks[0].start)} au {fmtDateFr(last.end)} ({devis.nights} nuits){devis.hasCar && <> — départ : <strong>{pickupLabel}</strong></>}</p>
                <DevisRecap recap={recap} annee={annee} prefill={prefill} inscriptionsOuvertes={weeks.every((w) => w.inscriptionOuverte)} incomplet={!devis.complete} bloque={devis.tooManyAdults} withInsurance={withInsurance} setWithInsurance={setWithInsurance} regles={{ ...regles, assurance: devis.prixAssurance }} />
              </div>
            </Step>
          </>
        )}
      </main>
      {recap && <DevisPrint recap={recap} annee={annee} />}
    </div>
  );
};

export default SpainSimulator;
